import { Directive, inject, input, OnDestroy, OnInit } from '@angular/core';
import { FormGroupDirective } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ENVIRONMENT } from '../../../environments/environment.interface';
import { LOGGER } from '../logging/log.service';
import { NOTIFICATION_SERVICE } from '../notification/notification';
import { FormPersistenceService } from './form-persistence.service';

@Directive({
	selector: '[lvrAutoSave]',
	standalone: true,
})
export class LvrAutoSaveDirective implements OnInit, OnDestroy {
	public readonly entityType = input.required<string>({ alias: 'lvrAutoSave' });
	public readonly entityId = input.required<string | number>();
	public readonly phase = input<string>();
	public readonly autoLoad = input<boolean>(true);
	public readonly autoLoadNotification = input<boolean>(true);

	private readonly destroy$ = new Subject<void>();

	private readonly environment = inject(ENVIRONMENT);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly log = inject(LOGGER);
	private readonly formGroupDirective = inject(FormGroupDirective);
	private readonly formPersistenceService = inject(FormPersistenceService);

	public ngOnInit() {
		this.formGroupDirective.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
			if (!this.formGroupDirective.form.dirty) return;
			this.formPersistenceService.autoSave(
				this.formGroupDirective.form,
				this.entityType(),
				String(this.entityId()),
				this.phase(),
			);
		});

		// If auto-load is enabled the latest state will be automatically loaded
		if (this.autoLoad()) this.loadDraft();
	}

	public ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private loadDraft() {
		const draft = this.formPersistenceService.loadDraft(this.entityType(), String(this.entityId()));
		this.log.log(`Loaded draft for ${this.entityType()} with ID ${this.entityId()}:`, false, draft);
		if (!draft) {
			console.warn(`No draft found for ${this.entityType()} with ID ${this.entityId()}`);
			return;
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		this.formGroupDirective.form.patchValue(draft.formData as any);
		if (!this.environment.draftStorageConfig.showToastOnLoad || !this.autoLoadNotification()) return;
		this.notificationService.addNotification({
			message: 'Daten wiederhergestellt',
			showToast: true,
			type: 'info',
			id: `draft-restored-${this.entityType()}-${this.entityId()}`,
			timestamp: new Date(),
		});
	}
}
